import React, { useMemo, useCallback } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button,
  Chip
} from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { LETTER_MAPPING } from '../constants';

interface LetterSelectorProps {
  selectedLetter: string | null;
  onLetterSelect: (letter: string) => void;
  letterProgress: Map<string, 'not_started' | 'in_progress' | 'completed'>;
}

const LetterSelector: React.FC<LetterSelectorProps> = React.memo(({
  selectedLetter,
  onLetterSelect,
  letterProgress
}) => {
  // Мемоизируем список букв, так как он не изменяется
  const arabicLetters = useMemo(() => Array.from(LETTER_MAPPING.keys()), []);

  // Мемоизируем функции для определения цвета и текста прогресса
  const getProgressColor = useCallback((progress: 'not_started' | 'in_progress' | 'completed') => {
    switch (progress) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      default:
        return 'default';
    }
  }, []);

  const getProgressText = useCallback((progress: 'not_started' | 'in_progress' | 'completed') => {
    switch (progress) {
      case 'completed':
        return 'Завершено';
      case 'in_progress':
        return 'В процессе';
      default:
        return 'Не начато';
    }
  }, []);

  // Мемоизируем обработчик клика
  const handleLetterClick = useCallback((letter: string) => {
    onLetterSelect(letter);
  }, [onLetterSelect]);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">
            Выберите букву для изучения
          </Typography>
          
          {selectedLetter && (
            <Chip 
              label={`Выбрано: ${selectedLetter}`}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Выберите арабскую букву. В справочных уроках показаны имена и доступные формы букв.
          Доступность звука и упражнений указана внутри урока.
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {arabicLetters.map((letter) => {
            const progress = letterProgress.get(letter) || 'not_started';
            const isSelected = selectedLetter === letter;
            
            return (
              <Box sx={{ flex: '1 1 120px', minWidth: '120px' }} key={letter}>
                <Button
                  variant={isSelected ? 'contained' : 'outlined'}
                  onClick={() => handleLetterClick(letter)}
                  sx={{
                    width: '100%',
                    height: '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontFamily: 'Noto Naskh Arabic, serif',
                    position: 'relative'
                  }}
                >
                  <Typography variant="h4" sx={{ fontFamily: 'Noto Naskh Arabic, serif' }}>
                    {letter}
                  </Typography>
                  
                  <Chip
                    label={getProgressText(progress)}
                    color={getProgressColor(progress)}
                    size="small"
                    sx={{ 
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      fontSize: '0.7rem',
                      height: '20px'
                    }}
                  />
                </Button>
              </Box>
            );
          })}
        </Box>
        
        {selectedLetter && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              endIcon={<PlayArrow />}
              onClick={() => onLetterSelect(selectedLetter)}
              size="large"
            >
              Начать урок с буквой {selectedLetter}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
});

LetterSelector.displayName = 'LetterSelector';

export default LetterSelector;
